import { useMemo } from "react";
import { Tooltip } from "@mui/material";
import { FlightRefund, FlightRefundStatus } from "@/types";
import { truncateAddress, truncateText } from "@/utils/truncateText";
import { tooltipStyles } from "@/utils/tooltipStyles";
import dayjs from "dayjs";
import {
  // getCancellationPolicyText,
  getNetworkDisplayName,
} from "@/utils/utils";

interface UseRefundColumnsProps {
  data: FlightRefund[];
  onAction: (refund: FlightRefund, status: FlightRefundStatus) => void;
}

export const useRefundFlightColumns = ({ data, onAction }: UseRefundColumnsProps) => {
  return useMemo(
    () =>
      data?.map((refund) => ({
        bookingId_Date: (
          <div className="flex flex-col gap-1">
            <span className="text-[16px] font-medium text-text">
              {refund.flightOrder.orderId}
            </span>
            <span className="text-sm text-[#334155]">
              {dayjs(refund.flightOrder.createdAt).format("YYYY-MM-DD HH:mm:ss")}
            </span>
          </div>
        ),
        contactPerson: (() => {
          if (refund.emailContact.length > 15) {
            return (
              <Tooltip
                title={refund.emailContact}
                placement="right"
                arrow
                componentsProps={{
                  tooltip: { sx: tooltipStyles.tooltip },
                  arrow: { sx: tooltipStyles.arrow },
                }}
              >
                <span className="text-[16px] font-medium text-text">
                  {truncateText(refund.emailContact, 15)}
                </span>
              </Tooltip>
            );
          }
          return (
            <span className="text-[16px] font-medium text-text ">
              {refund.emailContact}
            </span>
          );
        })(),
        amountOfRefund: (
          <div className="flex flex-col ">
            <span className="text-[16px] font-medium text-text ">
              {[FlightRefundStatus.PENDING_ADMIN, FlightRefundStatus.PENDING_FLINK].includes(refund.status) ? `${refund.refundAmountFlink} USD` : `${refund.refundAmount} USD`}
            </span>
          </div>
        ),
        addressToRefund: (() => {
          if (refund?.walletAddress && refund?.walletAddress?.length > 15) {
            return (
              <Tooltip
                title={refund?.walletAddress}
                placement="right"
                arrow
                componentsProps={{
                  tooltip: { sx: tooltipStyles.tooltip },
                  arrow: { sx: tooltipStyles.arrow },
                }}
              >
                <div className="flex flex-col">
                  {!refund?.walletAddress ? (
                    "-"
                  ) : (
                    <>
                      <span className="text-[16px] font-medium text-text ">
                        {truncateAddress(refund?.walletAddress)}
                      </span>
                      <span className="text-sm text-[#334155]">
                        {refund?.network &&
                          getNetworkDisplayName(refund?.network)}
                      </span>
                    </>
                  )}
                </div>
              </Tooltip>
            );
          }
          return (
            <div className="flex flex-col">
              {!refund?.walletAddress ? (
                <>
                  <span className="text-[16px] text-text ">
                    {refund?.bankAccountNumber}
                  </span>
                  <span className="text-sm text-[#334155]">
                    {refund?.bankName}
                  </span>
                </>
              ) : (
                <>
                  <span className="text-[16px] text-text ">
                    {refund?.walletAddress}
                  </span>
                  <span className="text-sm text-[#334155]">
                    {refund?.network &&
                      getNetworkDisplayName(refund?.network)}
                  </span>
                </>
              )}
            </div>
          );
        })(),
        requestDate: (
          <span className="text-sm font-semibold text-text">
            {dayjs(refund.createdAt).format("YYYY/MM/DD")}
          </span>
        ),
        // cancellationPolicy: (
        //   <span className="text-sm font-semibold text-text">
        //     {/* {getCancellationPolicyText(
        //       refund.cancelRestrictionType,
        //       refund.requestDate,
        //       refund.checkInDate,
        //       refund?.cancelRestrictionDay || 0,
        //       refund.cancelRestrictionTime,
        //     )} */}
        //   </span>
        // ),
        action: (() => {
          switch (refund.status.toUpperCase()) {
            case FlightRefundStatus.PENDING_FLINK:
              return (
                <button
                  onClick={() => onAction(refund, FlightRefundStatus.PENDING_FLINK)}
                  className="w-[165px] rounded-lg bg-text py-2 text-[16px] font-medium text-white"
                >
                  Flight Review
                </button>
              );
            case FlightRefundStatus.PENDING_USER_CONFIRM:
              return (
                <button
                  onClick={() => onAction(refund, FlightRefundStatus.PENDING_USER_CONFIRM)}
                  className="w-[165px] rounded-lg bg-text py-2 text-[16px] font-medium text-white"
                >
                  User Confirm
                </button>
              );
            case FlightRefundStatus.PENDING_ADMIN:
              return (
                <button
                  onClick={() => onAction(refund, FlightRefundStatus.PENDING_ADMIN)}
                  className="w-[165px] rounded-lg bg-text py-2 text-[16px] font-medium text-white"
                >
                  Review
                </button>
              );
            case FlightRefundStatus.USER_APPROVED:
              return (
                <button
                  onClick={() => onAction(refund, FlightRefundStatus.USER_APPROVED)}
                  className="w-[165px] rounded-lg bg-text py-2 text-[16px] font-medium text-white"
                >
                  Complete Refund
                </button>
              );
            case FlightRefundStatus.REJECTED:
              return (
                <button
                  onClick={() => onAction(refund, FlightRefundStatus.REJECTED)}
                  className="w-[165px] rounded-lg bg-[#F75C5C] py-2 text-[16px] font-medium text-white"
                >
                  Reject
                </button>
              );
            case FlightRefundStatus.COMPLETED:
              return (
                <button
                  onClick={() => onAction(refund, FlightRefundStatus.COMPLETED)}
                  className="w-[165px] rounded-lg bg-text py-2 text-[16px] font-medium text-white"
                >
                  {refund?.transactionId ? "Completed" : "Complete Refund"} 
                </button>
              );
            case FlightRefundStatus.TIMEOUT_CLOSED:
              return (
                <button
                  onClick={() => onAction(refund, FlightRefundStatus.TIMEOUT_CLOSED)}
                  className="w-[165px] rounded-lg bg-text py-2 text-[16px] font-medium text-white"
                >
                  Timeout
                </button>
              );
            case FlightRefundStatus.FAILED:
              return (
                <button
                  onClick={() => onAction(refund, FlightRefundStatus.FAILED)}
                  className="w-[165px] rounded-lg bg-[#F75C5C] py-2 text-[16px] font-medium text-white"
                >
                  Failed
                </button>
              );
            default:
              return (
                <button
                  onClick={() => onAction(refund, FlightRefundStatus.PENDING_ADMIN)}
                  className="w-[165px] rounded-lg bg-text py-2 text-[16px] font-medium text-white"
                >
                  Review
                </button>
              );
          }
        })(),
      })),
    [data],
  );
};
